/* eslint-disable */
import { T, useTheme } from "../tokens";
import { InspectorToolbar } from "./Inspector";

const VIEWPORT_OPTS = [
  { id: "desktop", icon: "🖥️", label: "Desktop" },
  { id: "tablet", icon: "📱", label: "Tablet" },
  { id: "mobile", icon: "📲", label: "Mobile" },
];

/** Unified wizard topbar + layout shell for all UX wizards. */
export default function WizardChrome({
  dark,
  setDark,
  title,
  subtitle,
  domain,
  sections,
  section,
  onSectionChange,
  viewport,
  onViewportChange,
  sidebarOpen,
  onSidebarToggle,
  showSidebarToggle = true,
  flowSidebar,
  inspectorPanel,
  inspectorOpen,
  children,
}) {
  const th = useTheme(dark);
  const showFlowSidebar = Boolean(flowSidebar) && sidebarOpen;
  const canvasPadding = inspectorOpen ? "12px 16px" : "22px 26px";
  const fullWidthDesktop = viewport === "desktop" && !showFlowSidebar && !inspectorOpen;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: th.bg,
        color: th.text,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        fontSize: 14,
        lineHeight: 1.5,
        overflow: "hidden",
        transition: th.trSlow,
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />
      <header
        style={{
          height: 48,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 14px",
          borderBottom: `1px solid ${th.border}`,
          background: dark ? "rgba(17,19,24,0.98)" : "rgba(255,255,255,0.98)",
          backdropFilter: "blur(12px)",
          flexShrink: 0,
          zIndex: 50,
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
          {showSidebarToggle && (
            <button
              type="button"
              onClick={onSidebarToggle}
              style={{ background: "none", border: "none", cursor: "pointer", color: th.textSec, padding: 4, fontSize: 14 }}
            >
              ☰
            </button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: T.brand,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              Z
            </div>
            <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>{title}</span>
            {domain && <span style={{ color: th.textMuted, fontSize: 10, whiteSpace: "nowrap" }}>· {domain}</span>}
            {subtitle && <span style={{ color: th.textMuted, fontSize: 10, whiteSpace: "nowrap" }}>{subtitle}</span>}
          </div>
          <div style={{ width: 1, height: 16, background: th.border, flexShrink: 0 }} />
          <nav style={{ display: "flex", gap: 2, overflow: "auto", minWidth: 0 }}>
            {sections.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onSectionChange(tab.id)}
                style={{
                  background: section === tab.id ? T.brandSoft : "none",
                  border: section === tab.id ? `1px solid ${T.brandBorder}` : "1px solid transparent",
                  borderRadius: 6,
                  padding: "4px 9px",
                  cursor: "pointer",
                  color: section === tab.id ? T.brand : th.textSec,
                  fontSize: 11,
                  fontWeight: section === tab.id ? "500" : "400",
                  transition: th.tr,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <div style={{ display: "flex", background: th.muted, borderRadius: 6, padding: "2px", gap: 1 }}>
            {VIEWPORT_OPTS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                title={opt.label}
                onClick={() => onViewportChange(opt.id)}
                style={{
                  background: viewport === opt.id ? th.surface : "none",
                  border: viewport === opt.id ? `1px solid ${th.border}` : "1px solid transparent",
                  borderRadius: 5,
                  padding: "2px 7px",
                  cursor: "pointer",
                  fontSize: 10,
                  color: viewport === opt.id ? th.text : th.textMuted,
                  transition: th.tr,
                }}
              >
                {opt.icon}
              </button>
            ))}
          </div>
          <InspectorToolbar dark={dark} />
          <button
            type="button"
            onClick={() => setDark((value) => !value)}
            style={{
              background: dark ? T.brandSoft : th.muted,
              border: `1px solid ${dark ? T.brandBorder : th.border}`,
              borderRadius: 16,
              padding: "4px 10px",
              cursor: "pointer",
              color: dark ? T.brand : th.textSec,
              fontSize: 11,
              fontWeight: 500,
              transition: th.tr,
            }}
          >
            {dark ? "🌙" : "☀️"}
          </button>
        </div>
      </header>

      <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
        {showFlowSidebar && (
          <aside
            style={{
              width: 190,
              flexShrink: 0,
              overflow: "auto",
              background: th.sidebar,
              borderRight: `1px solid ${th.border}`,
              padding: "10px 0",
            }}
          >
            {flowSidebar}
          </aside>
        )}
        <div style={{ flex: 1, display: "flex", minWidth: 0, minHeight: 0 }}>
          <main
            style={{
              flex: 1,
              overflow: "auto",
              padding: canvasPadding,
              minWidth: 0,
              maxWidth: fullWidthDesktop ? "none" : undefined,
            }}
          >
            {children}
          </main>
          {inspectorPanel}
        </div>
      </div>
    </div>
  );
}
